import { OctagonX } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

interface CancelTaskConfirmationDialogProps {
  /** Controls whether the dialog is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** Performs the actual cancel (calls the existing deny/cancel action) */
  onConfirm: () => void;
  /** Display name of the agent (e.g. "Frank"); never hardcode */
  agentName: string;
  /** Optional subject of the task being cancelled, shown in the title */
  taskSubject?: string;
}

/**
 * Confirmation dialog shown before cancelling an agent task. Warns that
 * cancelling disrupts the agent's current work and suggests re-assigning via
 * the thread instead. Used at every cancel site.
 * @param open - Whether the dialog is open
 * @param onOpenChange - Callback when the open state changes
 * @param onConfirm - Runs the actual cancel when the user confirms
 * @param agentName - Display name of the agent, interpolated into the copy
 * @param taskSubject - Optional task subject shown in the title
 */
export function CancelTaskConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  agentName,
  taskSubject,
}: CancelTaskConfirmationDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-start gap-3.5 text-left">
            <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-50 motion-safe:duration-500 motion-safe:delay-100 motion-safe:ease-out motion-safe:fill-mode-backwards">
              <OctagonX className="size-5" />
            </span>
            <div className="space-y-1.5">
              <AlertDialogTitle className="leading-snug">
                {taskSubject ? `Cancel "${taskSubject}"?` : "Cancel this task?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                Heads up, this stops {agentName} mid-task, and it can't be
                undone. If you'd rather change direction, just reply to{" "}
                {agentName} in the thread instead.
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Keep task</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={buttonVariants({ variant: "destructive" })}
          >
            Cancel task
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
